import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import type { Load } from "@shared/schema";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type SortColumn =
  | "company"
  | "origin"
  | "destination"
  | "weight"
  | "price"
  | "pickupDate"
  | "status"
  | "createdAt";

type SortDirection = "asc" | "desc";
type AdminLoad = Load & { company: string | null };

interface AdminLoadsResponse {
  loads: AdminLoad[];
  totalCount: number;
  page: number;
  pageSize: number;
}

const PAGE_SIZE = 25;

export default function AdminLoads() {
  const { toast } = useToast();
  const [sortBy, setSortBy] = useState<SortColumn>("createdAt");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const [page, setPage] = useState(1);
  const queryParams = new URLSearchParams({
    sortBy,
    sortDir,
    page: String(page),
    pageSize: String(PAGE_SIZE),
  });
  const loadsQueryKey = `/api/admin/loads?${queryParams.toString()}`;

  const { data, isLoading, error } = useQuery<AdminLoadsResponse>({
    queryKey: [loadsQueryKey],
  });

  const deleteLoad = useMutation({
    mutationFn: async (loadId: string) => {
      return apiRequest("DELETE", `/api/admin/loads/${loadId}`, {});
    },
    onSuccess: async () => {
      if (data?.loads.length === 1 && page > 1) {
        setPage((currentPage) => Math.max(1, currentPage - 1));
      }
      await queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          query.queryKey[0].startsWith("/api/admin/loads"),
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
      toast({
        title: "Success",
        description: "Load deleted successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete load",
        variant: "destructive",
      });
    },
  });

  const handleSort = (column: SortColumn) => {
    setPage(1);
    if (sortBy === column) {
      setSortDir((currentDirection) => currentDirection === "asc" ? "desc" : "asc");
      return;
    }
    setSortBy(column);
    setSortDir("asc");
  };

  const renderSortableHeader = (label: string, column: SortColumn) => (
    <TableHead>
      <Button
        type="button"
        variant="ghost"
        className="-ml-3 h-8 px-3 font-medium"
        onClick={() => handleSort(column)}
        aria-label={`Sort by ${label}`}
      >
        {label}
        {sortBy === column && (
          sortDir === "asc"
            ? <ArrowUp className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
            : <ArrowDown className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
        )}
      </Button>
    </TableHead>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-lg text-muted-foreground">Loading loads...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-lg text-destructive mb-2">Access Denied</div>
          <div className="text-sm text-muted-foreground">You do not have permission to access the admin panel.</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-lg text-muted-foreground">No loads found</div>
        </div>
      </div>
    );
  }

  const { loads, totalCount, pageSize } = data;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-wide" data-testid="text-admin-loads-title">Load Management</h1>
        <p className="text-muted-foreground">Manage and moderate posted loads</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Loads ({totalCount})</CardTitle>
          <CardDescription>Complete list of posted loads</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                {renderSortableHeader("Company", "company")}
                {renderSortableHeader("Origin", "origin")}
                {renderSortableHeader("Destination", "destination")}
                {renderSortableHeader("Weight (lbs)", "weight")}
                {renderSortableHeader("Price", "price")}
                {renderSortableHeader("Pickup Date", "pickupDate")}
                {renderSortableHeader("Status", "status")}
                {renderSortableHeader("Created Date", "createdAt")}
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loads.map((load) => (
                <TableRow key={load.id} data-testid={`row-load-${load.id}`}>
                  <TableCell>{load.company || "—"}</TableCell>
                  <TableCell className="font-medium">{load.origin}</TableCell>
                  <TableCell>{load.destination}</TableCell>
                  <TableCell data-testid={`text-weight-${load.id}`}>{load.weight.toLocaleString()}</TableCell>
                  <TableCell data-testid={`text-price-${load.id}`}>${load.price}</TableCell>
                  <TableCell>
                    {new Date(load.pickupDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={load.status === "Available" ? "default" : "secondary"}
                      data-testid={`badge-status-${load.id}`}
                    >
                      {load.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{new Date(load.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`button-delete-load-${load.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Load</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this load? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteLoad.mutate(load.id)}
                            data-testid="button-confirm-delete"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
            <p className="text-sm text-muted-foreground">
              {rangeStart}–{rangeEnd} of {totalCount} loads
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
              >
                <ChevronLeft className="mr-1 h-4 w-4" aria-hidden="true" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages || isLoading}
                onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
              >
                Next
                <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
